import { ChangeEvent, FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton } from "../../../../components/CustomButton/CustomButton";
import useStyles from "./styles";
import { CustomTextInput } from "../../../../components/CustomTextInput/CustomTextInput";
import { CustomSearch } from "../../../../components/CustomSearch/CustomSearch";
import { CustomTextDatePicker } from "../../../../components/CustomTextDatePicker/CustomTextDatePicker";
import { useFormik } from "formik";
import {
  initialValuesSinister,
  validationSchemaCreateSinister,
} from "./validationSchema";
import dayjs from "dayjs";
import { getCurrentFleetId } from "../../../../utils/getCurrentFleet";
import { useGetAllVehiculesFleet } from "../../../../api/vehicule";
import { CustomTextSelect } from "../../../../components/CustomTextSelect/CustomTextSelect";
import { getSinisterStatusOptions } from "../../types";
import { useCreateSinister } from "../../../../api/sinistre";
import { colors } from "../../../../styles/colors";

type PresetVehicle = {
  vehicleId: number;
  brand?: string;
  brandModel?: string;
  immatriculation?: string;
};

type Props = {
  onClickCreate?: () => void;
  presetVehicle?: PresetVehicle;
};

export const StepCreateClaims: FC<Props> = ({ onClickCreate, presetVehicle }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  // Libelles traduits, valeurs = codes. CustomTextSelect supporte deja les
  // options { label, value }, donc rien en aval n'a a interpreter une chaine
  // d'affichage.
  const statusOptions = useMemo(() => getSinisterStatusOptions(t), [t]);
  // A sinistre (claim) can only be filed against a vehicle that is currently
  // ASSIGNED to a collaborator: the backend
  // (Sinistre::createFleetVehicleAssignedSinister) requires a
  // FleetVehicleAssigned row and links the claim to that driver, otherwise it
  // throws "Vehicle not assigned to employee". So we list Affected vehicles
  // (status "A"), not Free ones ("F"). Filtering by "F" previously hid every
  // assignable vehicle and surfaced only ones the backend would reject.
  const { vehicules, isLoading: isLoadingVehicles } = useGetAllVehiculesFleet(
    getCurrentFleetId(),
    1,
    5000,
    "",
    "A"
  );

  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(
    presetVehicle?.vehicleId ?? null
  );

  const presetVehicleLabel = presetVehicle
    ? `${presetVehicle.brand ?? ""} ${presetVehicle.brandModel ?? ""}`.trim() +
      (presetVehicle.immatriculation
        ? ` - ${presetVehicle.immatriculation}`
        : "")
    : "";

  // A claim can only be filed against a vehicle currently ASSIGNED to a
  // collaborator (the assignable list is fetched with status "A"). If we were
  // handed a preset vehicle that is not in that list, block submission and
  // explain why, instead of letting the backend reject it after the fact.
  const presetNotAssignable =
    !!presetVehicle?.vehicleId &&
    !isLoadingVehicles &&
    !(vehicules ?? []).some(
      (v: any) => v.vehicleId === presetVehicle?.vehicleId
    );

  const onSuccessCreate = () => {
    onClickCreate && onClickCreate();
  };

  const { mutate: createSinister, isPending } = useCreateSinister(
    getCurrentFleetId(),
    selectedVehicleId ?? 0,
    onSuccessCreate
  );

  const formik = useFormik({
    initialValues: initialValuesSinister,
    validationSchema: validationSchemaCreateSinister,
    onSubmit: (values) => {
      createSinister({
        name: values.name ?? "",
        description: values.description ?? "",
        mileage: values.mileage ?? "",
        // Le code part tel quel. Il etait converti depuis la cle d'enum
        // francaise ici, ce qui faisait transiter une chaine d'UI comme
        // format de transport d'un statut.
        sinister_status: values.sinister_status ?? "B",
        sinister_at: values.sinister_at ?? "",
      });
    },
  });

  const onChangeTextFieldVehicle = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.value.trim().length === 0) {
      setSelectedVehicleId(null);
    }
  };

  const onChangeAutoCompletVehicle = (_: any, vehicle: any) => {
    setSelectedVehicleId(vehicle.vehicleId);
  };

  const handleSave = () => {
    if (presetNotAssignable) return;
    formik.handleSubmit();
  };

  return (
    <div className={classes.container}>
      <div className={classes.containerSearch}>
        <div className={classes.divInput}>
          <CustomTextInput
            label={`${t("claims.titleOfTheClaim", "Title of the claim")} *`}
            placeholder={t("claims.titleOfTheClaim", "Title of the claim")}
            name="name"
            value={formik?.values?.name}
            onChange={formik.handleChange}
          />
          {presetVehicle?.vehicleId ? (
            <CustomTextInput
              label={`${t("claims.vehicleSearch", "Vehicle search")} *`}
              value={presetVehicleLabel}
              disabled
            />
          ) : (
            <CustomSearch
              options={vehicules}
              getOptionLabel={(option: any) =>
                `${option?.brand} ${option?.brandModel} - ${option?.immatriculation}`
              }
              label={`${t("claims.vehicleSearch", "Vehicle search")} *`}
              placeholder={t("common.find", "Find")}
              onChange={onChangeAutoCompletVehicle}
              onChangeTextField={onChangeTextFieldVehicle}
            />
          )}
          {presetNotAssignable && (
            <div style={{ color: colors.tertiary, fontSize: 13, marginTop: 4 }}>
              {t(
                "claims.vehicleNotAssignedHint",
                "Assign a driver to this vehicle before filing a claim."
              )}
            </div>
          )}
          <CustomTextDatePicker
            label={`${t("claims.dateOfLoss", "Date of loss")} *`}
            value={formik.values.sinister_at as any}
            onChange={(value) =>
              formik?.setFieldValue(
                "sinister_at",
                dayjs(value).format("YYYY-MM-DD")
              )
            }
          />
          <CustomTextInput
            label={`${t("claims.mileage", "Mileage")} *`}
            placeholder={`${t("claims.mileage", "Mileage")} *`}
            type={"number"}
            name="mileage"
            value={formik?.values?.mileage}
            onChange={formik.handleChange}
          />
          <CustomTextSelect
            label={`${t("vehicule.status", "Status")} *`}
            options={statusOptions}
            placeholder={t("vehicule.status", "Status")}
            name="sinister_status"
            value={formik?.values?.sinister_status}
            onChange={formik.handleChange}
          />
          <CustomTextInput
            label={`${t("claims.comment", "Comment")} *`}
            placeholder={t("common.yourDetailsHere", "Your details here")}
            rows={5}
            name="description"
            value={formik?.values?.description}
            onChange={formik.handleChange}
          />
        </div>
        <CustomButton
          onClick={handleSave}
          label={t("claims.createTheClaim", "Create the claim")}
          variant={formik.isValid && formik.dirty && !presetNotAssignable ? "primary" : "disabled"}
          width={"fit-content"}
          height={"40px"}
          loading={isPending}
        />
      </div>
    </div>
  );
};
